import React, { useCallback, useEffect, useMemo, useState } from "react";
import { financeAPI, productsAPI, suppliersAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";
import useShopCurrency from "../hooks/useShopCurrency";
import { useDeliveryLines } from "../hooks/useDeliveryLines";
import { formatMoney } from "../pos/utils/money";
import { formatDate, todayIso as today } from "../utils/dates";
import { PRODUCT_CATEGORIES } from "../constants/categories";
import EmptyState from "../components/EmptyState";

const field =
  "mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";

/**
 * Recording what arrived from a supplier, what it cost, and what was paid.
 *
 * This is the only place buy prices get maintained after the initial catalogue
 * entry, so it has to be quicker than editing products one at a time: search,
 * pick, type the quantity and what the lot cost, next line. A lot can go in
 * without its price — the stock counts now — and be priced from the list
 * below later.
 */
const Deliveries = () => {
  const { t } = useI18n();
  const { can, moduleOn, isOwner, shop } = useMembership();
  const withWarehouse = Boolean(shop?.warehouse_enabled);
  const currency = useShopCurrency();
  const money = (value) => formatMoney(value, currency);
  const withSuppliers = moduleOn("suppliers");

  const [receipts, setReceipts] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [open, setOpen] = useState(false);

  const [header, setHeader] = useState({
    supplier_name: "",
    reference: "",
    received_on: today(),
    notes: "",
    paid_now: "",
    paid_from: "outside",
  });
  const delivery = useDeliveryLines();
  const [search, setSearch] = useState("");
  const [newProduct, setNewProduct] = useState(null);
  const [pricing, setPricing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, p, s] = await Promise.all([
        financeAPI.receipts({ limit: 30 }),
        productsAPI.getAll(),
        withSuppliers ? suppliersAPI.names().catch(() => ({ data: [] })) : { data: [] },
      ]);
      setReceipts(r.data);
      setProducts(p.data);
      setSuppliers(s.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("finance.deliveries.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withSuppliers]);

  useEffect(() => {
    load();
  }, [load]);

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];

    const chosen = new Set(delivery.lines.map((line) => line.product_id));

    return products
      .filter(
        (product) =>
          !chosen.has(product.id) &&
          `${product.name} ${product.brand || ""}`.toLowerCase().includes(term),
      )
      .slice(0, 6);
  }, [search, products, delivery.lines]);

  const { summary } = delivery;
  const supplierNamed = header.supplier_name.trim() !== "";

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      await financeAPI.createReceipt({
        supplier_name: header.supplier_name || undefined,
        reference: header.reference || undefined,
        received_on: header.received_on,
        notes: header.notes || undefined,
        items: delivery.toItems({ withWarehouse }),
        paid_now: supplierNamed && header.paid_now !== "" ? parseFloat(header.paid_now) : undefined,
        paid_from: header.paid_from,
      });

      setNotice(
        summary.unpriced > 0
          ? t("finance.deliveries.recordedAwaiting")
          : t("finance.deliveries.recorded"),
      );
      delivery.reset();
      setHeader({ ...header, reference: "", notes: "", paid_now: "" });
      setOpen(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("finance.deliveries.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const savePrices = async (receipt) => {
    setBusy(true);
    setError("");

    try {
      await financeAPI.priceReceipt(
        receipt.id,
        Object.entries(pricing.values)
          .filter(([, value]) => value !== "")
          .map(([itemId, value]) => ({ item_id: itemId, line_total: parseFloat(value) })),
      );
      setPricing(null);
      setNotice(t("finance.deliveries.pricesSaved"));
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("finance.deliveries.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t("finance.deliveries.title")}
          </h1>
          <p className="text-gray-600">{t("finance.deliveries.subtitle")}</p>
        </div>
        {can("deliveries.edit") && (
          <button
            onClick={() => setOpen((value) => !value)}
            className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
          >
            {open ? t("common.cancel") : t("finance.deliveries.record")}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {notice && !error && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      {open && (
        <form onSubmit={submit} className="bg-white rounded-xl shadow-lg p-6 space-y-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("finance.deliveries.supplier")}
              </span>
              <input
                type="text"
                list="delivery-suppliers"
                autoComplete="off"
                value={header.supplier_name}
                onChange={(e) => setHeader({ ...header, supplier_name: e.target.value })}
                className={field}
              />
              <datalist id="delivery-suppliers">
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.name} />
                ))}
              </datalist>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("finance.deliveries.invoice")}
              </span>
              <input
                type="text"
                value={header.reference}
                onChange={(e) => setHeader({ ...header, reference: e.target.value })}
                className={field}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("finance.deliveries.arrived")}
              </span>
              <input
                type="date"
                required
                value={header.received_on}
                onChange={(e) => setHeader({ ...header, received_on: e.target.value })}
                className={field}
              />
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("finance.deliveries.addProducts")}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("finance.deliveries.searchPlaceholder")}
                className="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="button"
                onClick={() => setNewProduct({ name: search.trim(), category: "juice" })}
                className="shrink-0 px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50"
              >
                ＋ {t("finance.deliveries.newProduct")}
              </button>
            </div>
            {matches.length > 0 && (
              <ul className="mt-1 border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
                {matches.map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      onClick={() => {
                        delivery.addProduct(product);
                        setSearch("");
                      }}
                      className="w-full text-start px-4 py-2 hover:bg-gray-50 flex justify-between gap-3"
                    >
                      <span>{product.name}</span>
                      <span className="text-sm text-gray-500 shrink-0">
                        {t("finance.deliveries.inStock", { count: product.stock_quantity })}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {newProduct && (
              <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-gray-300 p-3">
                <label className="flex-1 min-w-[10rem] text-sm text-gray-700">
                  {t("finance.deliveries.newProductName")}
                  <input
                    type="text"
                    autoFocus
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </label>
                <label className="text-sm text-gray-700">
                  {t("finance.deliveries.category")}
                  <select
                    value={newProduct.category}
                    onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                    className="mt-1 block px-3 py-2 border border-gray-300 rounded-lg bg-white"
                  >
                    {PRODUCT_CATEGORIES.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  disabled={!newProduct.name.trim()}
                  onClick={() => {
                    delivery.addNewProduct(newProduct.name, newProduct.category);
                    setNewProduct(null);
                    setSearch("");
                  }}
                  className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {t("finance.deliveries.addLine")}
                </button>
                <p className="w-full text-xs text-gray-500">{t("finance.deliveries.newProductHint")}</p>
              </div>
            )}
          </div>

          {delivery.lines.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[28rem]">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-start px-3 py-2">{t("finance.deliveries.columns.product")}</th>
                    <th className="text-end px-3 py-2 w-28">{t("finance.deliveries.columns.quantity")}</th>
                    {withWarehouse && (
                      <th className="text-start px-3 py-2 w-48">{t("finance.deliveries.columns.destination")}</th>
                    )}
                    <th className="text-end px-3 py-2 w-36">{t("finance.deliveries.columns.lotTotal")}</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {delivery.lines.map((line) => (
                    <tr key={line.key} className="border-t border-gray-100">
                      <td className="px-3 py-2">
                        {line.name}
                        {line.new_product && (
                          <span className="ms-2 text-xs font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                            {t("finance.deliveries.newBadge")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="1"
                          dir="ltr"
                          value={line.quantity}
                          onChange={(e) => delivery.setLine(line.key, { quantity: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-right"
                        />
                      </td>
                      {withWarehouse && (
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <select
                              aria-label={`${t("finance.deliveries.columns.destination")} — ${line.name}`}
                              value={line.destination}
                              onChange={(e) => delivery.setLine(line.key, { destination: e.target.value })}
                              className="px-2 py-1 border border-gray-300 rounded"
                            >
                              <option value="shop">{t("finance.deliveries.destination.shop")}</option>
                              <option value="warehouse">{t("finance.deliveries.destination.warehouse")}</option>
                              <option value="split">{t("finance.deliveries.destination.split")}</option>
                            </select>
                            {line.destination === "split" && (
                              <input
                                type="number"
                                min="1"
                                dir="ltr"
                                aria-label={`${t("finance.deliveries.toWarehouse")} — ${line.name}`}
                                placeholder={t("finance.deliveries.toWarehouse")}
                                value={line.to_warehouse}
                                onChange={(e) => delivery.setLine(line.key, { to_warehouse: e.target.value })}
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-right"
                              />
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          dir="ltr"
                          placeholder={t("finance.deliveries.later")}
                          value={line.line_total}
                          onChange={(e) => delivery.setLine(line.key, { line_total: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-right"
                        />
                      </td>
                      <td className="px-3 py-2 text-end">
                        <button
                          type="button"
                          onClick={() => delivery.removeLine(line.key)}
                          className="text-gray-400 hover:text-red-600"
                          aria-label={t("finance.deliveries.removeLine")}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={withWarehouse ? 3 : 2} className="px-3 py-2 text-end font-medium">
                      {t("common.total")}
                    </td>
                    <td className="px-3 py-2 text-end font-bold whitespace-nowrap">
                      {money(summary.total)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {summary.unpriced > 0 && (
            <p className="text-sm text-amber-700">
              {t("finance.deliveries.unpricedNote", { count: summary.unpriced })}
            </p>
          )}

          {withSuppliers && supplierNamed && (
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-sm font-medium text-gray-700">
                  {t("finance.deliveries.paidNow", { currency })}
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  dir="ltr"
                  placeholder="0"
                  value={header.paid_now}
                  onChange={(e) => setHeader({ ...header, paid_now: e.target.value })}
                  className={field}
                />
                <span className="text-xs text-gray-500">{t("finance.deliveries.paidNowHint")}</span>
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">
                  {t("finance.deliveries.paidFrom")}
                </span>
                <select
                  value={header.paid_from}
                  onChange={(e) => setHeader({ ...header, paid_from: e.target.value })}
                  className={`${field} bg-white`}
                >
                  <option value="outside">{t("finance.deliveries.fromOutside")}</option>
                  {can("power.manage_cash") && (
                    <option value="drawer">{t("finance.deliveries.fromDrawer")}</option>
                  )}
                  {isOwner && <option value="safe">{t("finance.deliveries.fromSafe")}</option>}
                </select>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={!summary.complete || busy}
            className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-50"
          >
            {busy ? t("finance.deliveries.recording") : t("finance.deliveries.submit")}
          </button>
          {delivery.lines.length > 0 && !summary.complete && (
            <p className="text-sm text-amber-700">{t("finance.deliveries.incomplete")}</p>
          )}
        </form>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        {loading ? (
          <p className="text-gray-500 py-8 text-center">{t("common.loading")}</p>
        ) : receipts.length === 0 ? (
          <EmptyState screen="deliveries" message={t("finance.deliveries.none")} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {receipts.map((receipt) => {
              const awaiting = receipt.status === "awaiting_prices";
              const pricingThis = pricing?.id === receipt.id;

              return (
                <li key={receipt.id} className="py-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-medium text-gray-900 flex flex-wrap items-center gap-2">
                      {receipt.supplier_name || receipt.supplier || t("finance.deliveries.unnamed")}
                      {receipt.reference && (
                        <span className="text-sm text-gray-500">#{receipt.reference}</span>
                      )}
                      {awaiting && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                          {t("finance.deliveries.awaiting")}
                        </span>
                      )}
                    </p>
                    <div className="flex items-center gap-3">
                      {receipt.total_cost !== undefined && (
                        <p className="font-semibold text-gray-900 whitespace-nowrap">
                          {money(receipt.total_cost)}
                        </p>
                      )}
                      {awaiting && can("deliveries.edit") && can("power.see_costs") && !pricingThis && (
                        <button
                          type="button"
                          onClick={() => setPricing({ id: receipt.id, values: {} })}
                          className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold"
                        >
                          {t("finance.deliveries.addPrices")}
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">
                    {formatDate(receipt.received_on)} ·{" "}
                    {t("common.products", { count: receipt.items.length })}
                  </p>
                  <ul className="mt-2 text-sm text-gray-600 space-y-1">
                    {receipt.items.map((item) => (
                      <li key={item.id} className="flex flex-wrap items-center justify-between gap-3">
                        <span>
                          {item.quantity} × {item.product_name}
                          {item.warehouse_quantity > 0 && (
                            <span className="ms-2 text-xs text-gray-500">
                              {item.warehouse_quantity === item.quantity
                                ? t("finance.deliveries.allWarehouse")
                                : t("finance.deliveries.partWarehouse", { count: item.warehouse_quantity })}
                            </span>
                          )}
                        </span>
                        {pricingThis && item.subtotal === null ? (
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            dir="ltr"
                            placeholder={t("finance.deliveries.columns.lotTotal")}
                            value={pricing.values[item.id] ?? ""}
                            onChange={(e) =>
                              setPricing({
                                ...pricing,
                                values: { ...pricing.values, [item.id]: e.target.value },
                              })
                            }
                            className="w-36 px-2 py-1 border border-gray-300 rounded text-right"
                          />
                        ) : item.subtotal === null ? (
                          <span className="text-amber-700 shrink-0">{t("finance.deliveries.noPriceYet")}</span>
                        ) : (
                          item.subtotal !== undefined && (
                            <span className="text-gray-500 shrink-0">
                              {money(item.subtotal)} ·{" "}
                              {t("finance.deliveries.each", { amount: money(item.unit_cost) })}
                            </span>
                          )
                        )}
                      </li>
                    ))}
                  </ul>
                  {pricingThis && (
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => savePrices(receipt)}
                        className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
                      >
                        {t("finance.deliveries.savePrices")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPricing(null)}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-sm"
                      >
                        {t("common.cancel")}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Deliveries;
